/**
 * Work Orders — the list, and one order in detail.
 *
 * Read-only in this phase. Writing an order is the builder screen, and it is
 * deliberately not squeezed in here: a work order with four pricing bases,
 * milestones that must sum to 100% and PAN-derived deductions is its own
 * screen, and half of one would be worse than none.
 *
 * What this DOES give you today is the thing the paper order never could:
 * every contract in one list, with its value, its programme and how far along
 * it actually is.
 */
import { useCallback, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  AlertTriangle, ArrowLeft, Copy, Eye, FileDown, FileSignature, FilterX, Landmark, ListChecks,
  Pencil, Plus, Ruler, Send, SlidersHorizontal, Stamp,
} from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { usePeople, useQuery, useSites } from '@/lib/data'
import { callFunction, downloadFromFunction, supabase } from '@/lib/supabase'
import { ddmmyyyy, qty, rupees } from '@/lib/format'
import {
  Card, EmptyState, Field, Loading, Modal, SearchInput, Select, StatusPill, Th, Input,
  Textarea, sortRows, useSort, useToast,
} from '@/components/ui'
import { AuditTrail } from '@/components/docs/shared'
import { supabase as db } from '@/lib/supabase'
import { serviceCan } from '../nav'
import { ApprovalChain } from '../components/ApprovalChain'
import { WoCloseOut } from '../components/WoCloseOut'
import { WoMilestones } from '../components/WoMilestones'
import { WoHire } from '../components/WoHire'
import { LineGstTable, LinesViewSwitch, useLinesView } from '../components/LineGst'
import { gstPayer, gstSplit } from '../lib/gst'
import { WoHirePayout } from '../components/WoHirePayout'
import { PANE_WIDTH, PdfPreviewPane, useSplitWidth, type PdfPreview } from '@/components/docs/PdfPreviewPane'
import { WoPapers } from '../components/WoPapers'
import { WoTerms } from '../components/WoTerms'
import { rcmSupplierClause } from '../lib/reverse-charge'

interface WoRow {
  id: string; site_id: string; site_name: string
  vendor_id: string; vendor_name: string; vendor_gstin: string | null; vendor_pan: string | null
  contract_nature: string; title: string; quotation_ref: string | null
  status: string; current_step: number
  start_date: string | null; completion_date: string | null; duration_days: number | null
  contract_value: number; gst_treatment: string | null; gst_amount: number
  total_with_gst: number; is_reverse_charge: boolean
  retention_pct: number; tds_pct: number | null; tds_section: string | null
  payment_terms: string | null
  advance_pct: number; advance_recovery_pct: number; defect_liability_months: number
  date_created: string; vendor_ack_at: string | null
  line_count: number; pricing_bases: string | null; days_overdue: number | null
}
interface ProgressRow {
  wo_line_id: string; wo_id: string; sl_no: number; pricing_basis: string
  description: string; uom: string; rate: number
  qty_ordered: number; qty_allowed: number; qty_certified: number
  qty_left_to_measure: number; qty_ready_to_bill: number
  value_ready_to_bill: number; pct_complete: number | null
}

const n = (v: unknown) => Number(v ?? 0) || 0

const BASIS_LABEL: Record<string, string> = {
  MEASURED_ITEM: 'Measured item',
  AREA_RATE: 'Area rate',
  LUMPSUM: 'Lumpsum milestone',
  DAY_RATE: 'Day rate',
  EQUIPMENT_HIRE: 'Equipment hire',
}

/**
 * THE FILTERS ON THE REGISTER.
 *
 * A work order register is asked six different questions, and until now it
 * answered one of them. The six, in the order people actually ask them:
 *
 *   what happened to WO-TR5-…            the search box
 *   what is live at Khubal               site
 *   what is on Sunny's sites             Project Manager
 *   what is waiting for approval         status
 *   what is over fifty lakh              value from / to
 *   what did we place in September       raised between
 *
 * All of them are applied to the SAME list at once, in memory, because the
 * register is a few hundred rows and a round trip per keystroke is worse than
 * filtering what is already here. If it ever runs to tens of thousands this
 * moves into the view; it is one function either way.
 *
 * The Project Manager filter reads sites.project_manager_id rather than
 * anything on the order, because that is where the answer lives - an order
 * belongs to a site, and a site belongs to a manager.
 */
const BLANK = {
  term: '', status: '', site: '', pm: '',
  valueFrom: '', valueTo: '', from: '', to: '',
}

export function WorkOrderList() {
  const { profile } = useAuth()
  const people = usePeople()
  const { data: allSites } = useSites()
  const [f, setF] = useState({ ...BLANK })
  const [showMore, setShowMore] = useState(false)
  const { sort, toggle } = useSort('date_created', 'desc')

  const set = (patch: Partial<typeof BLANK>) => setF((v) => ({ ...v, ...patch }))
  const dirty = Object.entries(f).some(([k, v]) => v !== (BLANK as any)[k])

  const { data: rows, loading } = useQuery<WoRow>(
    'v_work_order_register',
    () => supabase.from('v_work_order_register').select('*'),
    [], { cacheable: false })

  /** site id -> the Project Manager named on it. */
  const pmOfSite = useMemo(() => {
    const m: Record<string, string> = {}
    for (const s of allSites as any[]) if (s.project_manager_id) m[s.id] = s.project_manager_id
    return m
  }, [allSites])

  /** Only managers who actually have a site are worth offering. */
  const pmOptions = useMemo(() => {
    const ids = [...new Set(Object.values(pmOfSite))]
    return ids
      .map((id) => ({ value: id, label: people.name({ id }) }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [pmOfSite, people])

  const siteOptions = useMemo(
    () => [...new Set(rows.map((r) => r.site_id))]
      .map((id) => ({
        value: id,
        label: (allSites as any[]).find((s) => s.id === id)?.site_name ?? id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [rows, allSites])

  const shown = useMemo(() => {
    const t = f.term.trim().toLowerCase()
    const lo = f.valueFrom === '' ? null : Number(f.valueFrom)
    const hi = f.valueTo === '' ? null : Number(f.valueTo)

    const filtered = rows.filter((r) => {
      if (f.status && r.status !== f.status) return false
      if (f.site && r.site_id !== f.site) return false
      if (f.pm && pmOfSite[r.site_id] !== f.pm) return false

      // Value is judged on the total WITH GST, because that is the number a
      // director means when he says "anything over fifty lakh".
      const v = n(r.total_with_gst)
      if (lo !== null && v < lo) return false
      if (hi !== null && v > hi) return false

      // Raised between. Dates are ISO in the row, so a string compare is a
      // date compare, and the bounds are inclusive at both ends.
      const d = String(r.date_created ?? '').slice(0, 10)
      if (f.from && d < f.from) return false
      if (f.to && d > f.to) return false

      if (t && ![r.id, r.title, r.vendor_name, r.site_name, r.quotation_ref,
                 r.vendor_pan, r.vendor_gstin]
        .some((v2) => String(v2 ?? '').toLowerCase().includes(t))) return false

      return true
    })
    return sortRows(filtered, sort, {
      contract_value: 'number', total_with_gst: 'number', line_count: 'number',
      date_created: 'date', completion_date: 'date',
    })
  }, [rows, f, pmOfSite, sort])

  const total = shown.reduce((a, r) => a + n(r.total_with_gst), 0)

  if (loading) return <Loading label="Reading the work orders…" />

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Work Orders</h1>
          <p className="text-sm text-slate-500">
            Contracts with works contractors. {shown.length}
            {dirty && <> of {rows.length}</>} shown, {rupees(total)} in total.
          </p>
        </div>
        {serviceCan(profile?.role, 'createWorkOrder') && (
          <Link to="/service/work-orders/new" className="btn-primary">
            <Plus className="h-4 w-4" /> New work order
          </Link>
        )}
      </div>

      <Card title={null}>
        <div className="mb-3 space-y-2">
          <div className="flex flex-wrap gap-2">
            <SearchInput value={f.term} onChange={(v) => set({ term: v })}
                         placeholder="Order number, title, contractor, site, PAN or GSTIN…"
                         className="min-w-[220px] flex-1" />
            <Select value={f.status} onChange={(e) => set({ status: e.target.value })}
                    className="w-44" placeholder="Any status"
                    options={[...new Set(rows.map((r) => r.status))].sort()
                      .map((s) => ({ value: s, label: s }))} />
            <button className={showMore ? 'btn-primary' : 'btn-secondary'}
                    onClick={() => setShowMore((v) => !v)}>
              <SlidersHorizontal className="h-4 w-4" />
              {showMore ? 'Fewer filters' : 'More filters'}
            </button>
            {dirty && (
              <button className="btn-ghost text-slate-600" onClick={() => setF({ ...BLANK })}
                      title="Clear every filter, including the ones that are hidden">
                <FilterX className="h-4 w-4" /> Clear all
              </button>
            )}
          </div>

          {showMore && (
            <div className="grid gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Site">
                <Select value={f.site} onChange={(e) => set({ site: e.target.value })}
                        placeholder="Every site" options={siteOptions} />
              </Field>
              <Field label="Project Manager" hint="The manager named on the site">
                <Select value={f.pm} onChange={(e) => set({ pm: e.target.value })}
                        placeholder="Every manager" options={pmOptions} />
              </Field>
              <div />
              <Field label="Value from" hint="Total with GST, in rupees">
                <Input type="number" min="0" step="1000" value={f.valueFrom}
                       placeholder="0"
                       onChange={(e) => set({ valueFrom: e.target.value })} />
              </Field>
              <Field label="Value to">
                <Input type="number" min="0" step="1000" value={f.valueTo}
                       placeholder="no limit"
                       onChange={(e) => set({ valueTo: e.target.value })} />
              </Field>
              <div />
              <Field label="Raised on or after">
                <Input type="date" value={f.from} onChange={(e) => set({ from: e.target.value })} />
              </Field>
              <Field label="Raised on or before">
                <Input type="date" value={f.to} onChange={(e) => set({ to: e.target.value })} />
              </Field>
            </div>
          )}

          {dirty && !showMore && (
            <p className="text-xs text-slate-500">
              Filters are on. <button className="font-medium text-amber-700 hover:underline"
                                      onClick={() => setShowMore(true)}>Show them</button>
              {' '}or <button className="font-medium text-amber-700 hover:underline"
                              onClick={() => setF({ ...BLANK })}>clear them</button>.
            </p>
          )}
        </div>

        {shown.length === 0 ? (
          <EmptyState icon={<FileSignature className="h-8 w-8" />}
                      title={dirty ? 'Nothing matches those filters' : 'No work orders'}
                      message={dirty
                        ? `${rows.length} order${rows.length === 1 ? '' : 's'} are hidden by the filters you have set.`
                        : 'None has been raised yet for a site you can see.'}
                      action={dirty
                        ? <button className="btn-secondary" onClick={() => setF({ ...BLANK })}>
                            <FilterX className="h-4 w-4" /> Clear all filters
                          </button>
                        : undefined} />
        ) : (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <Th id="id" sort={sort} onSort={toggle}>Work order</Th>
                  <Th id="site_name" sort={sort} onSort={toggle}>Site</Th>
                  <Th id="vendor_name" sort={sort} onSort={toggle}>Contractor</Th>
                  <Th id="line_count" sort={sort} onSort={toggle} align="right">Lines</Th>
                  <Th id="contract_value" sort={sort} onSort={toggle} align="right">Value</Th>
                  <Th id="total_with_gst" sort={sort} onSort={toggle} align="right">With GST</Th>
                  <Th id="status" sort={sort} onSort={toggle}>Status</Th>
                  <Th id="completion_date" sort={sort} onSort={toggle}>Due</Th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.id}>
                    <td className="whitespace-nowrap font-medium">
                      <Link to={`/service/work-orders/${r.id}`}
                            className="text-amber-700 hover:underline">{r.id}</Link>
                      <span className="block max-w-[240px] truncate text-[11px] text-slate-400">
                        {r.title}
                      </span>
                    </td>
                    <td className="max-w-[150px] truncate">{r.site_name}</td>
                    <td className="max-w-[170px] truncate">
                      {r.vendor_name}
                      {r.is_reverse_charge && <span className="pill-grey ml-1">RCM</span>}
                      {!r.vendor_pan && (
                        <span className="ml-1 text-amber-600" title="No PAN on file — TDS would be 20%">
                          <AlertTriangle className="inline h-3 w-3" />
                        </span>
                      )}
                    </td>
                    <td className="num">{r.line_count}</td>
                    <td className="num whitespace-nowrap">{rupees(r.contract_value)}</td>
                    <td className="num whitespace-nowrap font-semibold">{rupees(r.total_with_gst)}</td>
                    <td><StatusPill status={r.status} /></td>
                    <td className="whitespace-nowrap text-xs">
                      {r.completion_date ? ddmmyyyy(r.completion_date) : '—'}
                      {n(r.days_overdue) > 0 && (
                        <span className="ml-1 font-semibold text-red-600">+{r.days_overdue}d</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}

// ---------------------------------------------------------------------------

/**
 * WHAT THE TAX ON THIS ORDER IS, IN A SENTENCE THAT IS TRUE.
 *
 * Four states, and each one has to say something different. The old panel had
 * one sentence and showed it whenever is_reverse_charge was set, which was
 * safe only while "reverse charge" and "not registered" meant the same thing.
 * Since 0146 they do not: entry 14 of Notification 13/2017-CT(R) reaches a
 * REGISTERED security agency, and 0151 made the order header say so. The
 * sentence underneath was still the old one.
 *
 * The authority is quoted from the determination rather than written here, so
 * this panel cannot drift from the Reverse Charge screen or from the bill.
 */
function TaxTreatment({ wo, rcm }: { wo: WoRow; rcm: any[] }) {
  if (!rcm || rcm.length === 0) return null

  const registered = Boolean(wo.vendor_gstin)
  const verdicts = new Set(rcm.map((r) => String(r.verdict_from_list)))
  const overridden = rcm.some((r) => r.rcm_override)
  const ambiguous = rcm.some((r) => r.needs_a_decision)
  // "Section 9(3) CGST Act, Notification 13/2017-CT(R) entry 14" -> the two
  // parts the shared clause wants. Parsed rather than re-derived so the screen
  // quotes exactly what fn_rcm_decide decided.
  const authority: string = rcm.find((r) => r.verdict_from_list === 'RCM')?.authority
    ?? rcm[0]?.authority ?? ''
  const m = /Notification ([^ ]+) entry ([^ ,.]+)/.exec(authority)
  const notification = m?.[1] ?? null
  const entryNo = m?.[2] ?? null

  // An order still on the old rule: say nothing new, and keep the sentence it
  // has always had. wo.is_reverse_charge is the header's own answer.
  const onNotifiedList = verdicts.has('RCM') || verdicts.has('NO_TAX') || ambiguous

  if (ambiguous && !overridden) {
    return (
      <p className="mt-3 flex items-start gap-1.5 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-900">
        <Landmark className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          <strong>Nobody has settled the tax on this order.</strong>{' '}
          {rcm.find((r) => r.needs_a_decision)?.reason}{' '}
          Until it is settled the bill treats it as reverse charge, which is the
          cautious side. Settle it on <strong>Reverse Charge</strong>.
        </span>
      </p>
    )
  }

  if (wo.is_reverse_charge) {
    return (
      <p className="mt-3 flex items-start gap-1.5 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
        <Landmark className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          <strong>OVAL pays this tax to the Government directly, under reverse
          charge.</strong> It is not deducted from his bill and he must not charge
          it on his invoice.{' '}
          {rcmSupplierClause({
            registered,
            notification: notification,
            entryNo: entryNo,
          })}
          {registered && (
            <span className="mt-1 block">
              OVAL does <strong>not</strong> raise a self-invoice for this supply — his
              own invoice is the document, and two for one supply would declare the
              tax twice.
            </span>
          )}
        </span>
      </p>
    )
  }

  if (onNotifiedList && verdicts.has('NO_TAX') && !verdicts.has('FORWARD')) {
    return (
      <p className="mt-3 flex items-start gap-1.5 rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
        <Landmark className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          <strong>No GST arises on this order at all.</strong> He is not registered
          and this is not a service notified under section 9(3), so he charges none
          and OVAL owes none. Reverse charge under section 9(4) reaches only a
          promoter of a real estate project, which OVAL is not.
        </span>
      </p>
    )
  }

  if (verdicts.has('FORWARD') && registered) {
    return (
      <p className="mt-3 flex items-start gap-1.5 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-700">
        <Landmark className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          <strong>He charges the GST on his bill</strong> and pays it to the
          Government himself. OVAL claims it back as input credit.
          {verdicts.has('RCM') && ' Part of this order is reverse charge — see the line detail.'}
        </span>
      </p>
    )
  }

  return null
}

export function WorkOrderDetail() {
  const { id = '' } = useParams()
  const { profile } = useAuth()
  const navigate = useNavigate()
  const toast = useToast()
  const [acking, setAcking] = useState(false)
  const [ackRef, setAckRef] = useState('')
  const [raising, setRaising] = useState(false)
  const [voReason, setVoReason] = useState('')
  const [busy, setBusy] = useState(false)
  // Its own flag, not `busy`. Printing takes a second or two and must not grey
  // out Send to contractor while it runs — they are unrelated actions.
  const [pdfBusy, setPdfBusy] = useState(false)
  // CMD, 02-11-2026: the draft preview opens in a pane beside the page.
  const [preview, setPreview] = useState<PdfPreview | null>(null)
  const split = useSplitWidth(preview !== null)
  const closePreview = useCallback(() => setPreview(null), [])
  // 0219: copying this order into a new draft.
  const [duplicating, setDuplicating] = useState(false)

  const { data: rows, loading, refresh } = useQuery<WoRow>(
    `wo_${id}`,
    () => supabase.from('v_work_order_register').select('*').eq('id', id),
    [id], { cacheable: false })

  // The raw row, for the two fields the register view does not carry: who
  // created it (for the self-approval rule) and the site's Project Manager
  // (for stage 2). Both are needed to decide whether to draw a button.
  const { data: raw } = useQuery<any>(
    `wo_raw_${id}`,
    () => supabase.from('work_orders').select('created_by, site_id, copied_from').eq('id', id),
    [id], { cacheable: false })
  const { data: siteRow } = useQuery<any>(
    `wo_site_${id}`,
    () => supabase.from('sites').select('id, project_manager_id')
      .eq('id', raw[0]?.site_id ?? '__none__'),
    [raw[0]?.site_id], { cacheable: false, enabled: Boolean(raw[0]?.site_id) })

  /**
   * 0219: the whole order copied into a new DRAFT raised by this person -
   * header, lines, milestones, terms and papers (not the signed copy) - then
   * straight into the builder to correct it. The original is not touched.
   */
  async function duplicate() {
    setBusy(true)
    try {
      const { data, error } = await db.rpc('rpc_wo_duplicate', { p_wo_id: id })
      if (error) throw new Error(error.message)
      setDuplicating(false)
      toast.success(`Copied into a new draft, ${data}. Correct it, then submit it for approval.`)
      navigate(`/service/work-orders/${data}/edit`)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not duplicate it')
    } finally { setBusy(false) }
  }

  async function despatch() {
    setBusy(true)
    try {
      const { data, error } = await db.rpc('rpc_wo_send_to_contractor', { p_wo_id: id })
      if (error) throw new Error(error.message)
      toast.success(String(data)); refresh()
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Could not send it') }
    finally { setBusy(false) }
  }

  /**
   * The printed order.
   *
   * downloadFromFunction unwraps the edge function's {"error": "..."} into a
   * plain sentence, so a refusal — an order not yet approved, an order with no
   * lines — reaches the reader as words rather than as JSON braces.
   */
  async function downloadPdf() {
    setPdfBusy(true)
    try {
      await downloadFromFunction('generate-wo-pdf', { wo_id: id }, `${id}.pdf`)
      toast.success('Work order PDF downloaded.')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not generate the PDF')
    } finally { setPdfBusy(false) }
  }

  /**
   * CMD, 31-10-2026: the draft preview. No signature or seal, and "not a work
   * order" across every page - the edge function sees to it. Since 02-11-2026
   * it opens in a pane beside the page (PdfPreviewPane), not a new tab.
   */
  async function previewPdf() {
    setPdfBusy(true)
    try {
      const res = await callFunction('generate-wo-pdf', { wo_id: id, preview: true })
      if (!res.ok) {
        const text = (await res.text()).trim()
        let message = text
        try { const p = JSON.parse(text); if (typeof p?.error === 'string') message = p.error } catch { /* as sent */ }
        throw new Error(message || 'Could not prepare the preview')
      }
      setPreview({
        blob: await res.blob(),
        fileName: `${id}-DRAFT-PREVIEW.pdf`,
        title: `Draft preview - ${id}`,
      })
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not prepare the preview')
    } finally { setPdfBusy(false) }
  }

  /**
   * RAISE THE VARIATION FROM HERE — 17-09-2026.
   *
   * This button used to link to the variations register, where the first thing
   * asked was which work order — the one he was looking at when he pressed it.
   * The measurement page has raised one in a single click since 16-09-2026 and
   * this screen, which knows the order better than any other, was still
   * pointing at a menu.
   *
   * One decision is genuinely his and is asked for here: why. Everything else
   * is on the next screen, where the lines go.
   */
  async function raiseVariation() {
    setBusy(true)
    try {
      const { data, error } = await db.rpc('rpc_vo_raise',
        { p_wo_id: id, p_reason: voReason.trim() })
      if (error) throw new Error(error.message)
      const vo = String(data)
      toast.success(`${vo} raised. Put the lines on it, then submit.`)
      setRaising(false); setVoReason('')
      navigate(`/service/variations/${vo}/edit`)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not raise it')
    } finally { setBusy(false) }
  }

  async function acknowledge() {
    setBusy(true)
    try {
      const { data, error } = await db.rpc('rpc_wo_acknowledge',
        { p_wo_id: id, p_ref: ackRef.trim() || null })
      if (error) throw new Error(error.message)
      toast.success(String(data)); setAcking(false); setAckRef(''); refresh()
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Could not record it') }
    finally { setBusy(false) }
  }

  /**
   * What this order IS, from fn_wo_composition_label — the SAME function the
   * PDF calls. Reported 16-09-2026: the sheet said "labour with materials" on
   * an order of nothing but LABOUR ONLY lines, because both surfaces were
   * reading the header dropdown instead of the lines. One function now, so the
   * screen and the paper cannot disagree.
   */
  const { data: composition } = useQuery<any>(
    `wo_comp_${id}`,
    () => supabase.rpc('fn_wo_composition_label', { p_wo_id: id }).then(
      (r: any) => ({ data: r.data == null ? [] : [{ id, label: r.data }], error: r.error })),
    [id], { cacheable: false })

  const { data: lines } = useQuery<ProgressRow>(
    `wo_lines_${id}`,
    () => supabase.from('v_wo_line_progress').select('*').eq('wo_id', id).order('sl_no'),
    [id], { cacheable: false, idField: 'wo_line_id' })

  /**
   * WHERE THIS ORDER STANDS ON VARIATIONS — added 16-09-2026 with migration 0135.
   *
   * contract_value is what was SIGNED and never moves: the advance is a
   * percentage of it and the printed order carries it. What the order is worth
   * NOW is this plus every approved variation, and it is derived rather than
   * stored precisely so the two can never drift apart. See the header of
   * 20261115000001_variation_orders.sql.
   */
  const { data: vpos } = useQuery<any>(
    `wo_vpos_${id}`,
    () => supabase.from('v_wo_variation_position').select('*').eq('wo_id', id),
    [id], { cacheable: false, idField: 'wo_id' })

  const { data: variations } = useQuery<any>(
    `wo_vos_${id}`,
    () => supabase.from('v_variation_register').select('*').eq('wo_id', id)
      .order('variation_no'),
    [id], { cacheable: false })

  /**
   * Which lines arrived by variation rather than by the order.
   *
   * v_wo_line_progress deliberately does not carry it - it is a progress view,
   * not a provenance one - so the column is read straight off the table. A line
   * that reads "Ordered 0, Allowed 40" is otherwise a puzzle, and the answer is
   * one word long.
   */
  /**
   * WHY THE TAX ON THIS ORDER FALLS WHERE IT DOES.
   *
   * Read from v_rcm_determination -- the same view the Reverse Charge screen
   * reads, computed by the same fn_rcm_decide. Two screens explaining one
   * decision in two places is how they come to disagree, and this panel has
   * already disagreed once: until 20-09-2026 it said "This contractor is not
   * GST registered" on the strength of is_reverse_charge alone, and printed
   * that sentence over BIPLAB SHARMA, GSTIN 16CZRPS6127A1Z6, on a security
   * order where entry 14 makes OVAL liable however registered he is.
   */
  const { data: rcm } = useQuery<any>(
    `wo_rcm_${id}`,
    () => supabase.from('v_rcm_determination').select('*').eq('wo_id', id).order('sl_no'),
    [id], { cacheable: false, idField: 'wo_line_id' })

  const { data: lineOrigin } = useQuery<any>(
    `wo_line_origin_${id}`,
    () => supabase.from('wo_line_items').select('id, added_by_variation').eq('wo_id', id),
    [id], { cacheable: false })
  const bornOf = useMemo(() => {
    const m: Record<string, string> = {}
    for (const l of lineOrigin as any[]) if (l.added_by_variation) m[l.id] = l.added_by_variation
    return m
  }, [lineOrigin])

  // CMD, 31-10-2026: the Lines card's Progress / Money & GST switch.
  const [linesView, setLinesView] = useLinesView(rows[0]?.status)

  if (loading) return <Loading label="Reading the work order…" />
  const w = rows[0]
  if (!w) {
    return <EmptyState icon={<FileSignature className="h-8 w-8" />}
                       title="Not found"
                       message="No work order with that number, or it belongs to a site you cannot see." />
  }

  const D = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div>
      <dt className="text-xs font-medium text-slate-500">{label}</dt>
      <dd className="mt-0.5 break-words text-sm text-slate-800">{value ?? '—'}</dd>
    </div>
  )

  return (
    // On a large screen the page gives up the pane's width while a preview
    // is open, so the order and its PDF sit side by side.
    <div className="space-y-4 transition-[margin] duration-300 ease-out motion-reduce:transition-none"
         style={{ marginRight: split ? PANE_WIDTH : undefined }}>
      <Link to="/service/work-orders"
            className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft className="h-4 w-4" /> All work orders
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-slate-900">{w.id}</h1>
          <p className="text-sm text-slate-600">{w.title}</p>
          <p className="mt-1 text-xs text-slate-500">
            {w.site_name} · {w.vendor_name}
          </p>
          {raw[0]?.copied_from && (
            <p className="mt-1 text-xs text-slate-500">
              Copied from{' '}
              <Link to={`/service/work-orders/${raw[0].copied_from}`}
                    className="font-medium text-oval-700 hover:underline">
                {raw[0].copied_from}
              </Link>
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill status={w.status} />
          {/* The printed order, for anybody who can see it.
              Deliberately NOT role-gated: if a person is allowed to read this
              work order they are allowed to print it, and a Project Manager
              who cannot hand his own contractor a copy would come looking for
              somebody who can. The STATUS gate is the real control — the PDF
              carries the Project Head's seal, so the edge function refuses
              anything that has not cleared the chain, and the button is not
              drawn for those statuses either. */}
          {['APPROVED', 'VENDIN', 'VENDACK', 'CLOSED'].includes(w.status) && (
            <button className="btn-secondary btn-sm" disabled={pdfBusy}
                    onClick={() => void downloadPdf()}>
              <FileDown className="h-4 w-4" />
              {pdfBusy ? 'Preparing…' : 'Download PDF'}
            </button>
          )}
          {/* CMD, 31-10-2026: the same document before approval, unsigned and
              watermarked, for anybody who can see the order. */}
          {['DRAFT', 'SUBMITTED', 'RETURNED'].includes(w.status) && (
            <button className="btn-secondary btn-sm" disabled={pdfBusy}
                    title="Opens the printable order in a new tab - unsigned and marked DRAFT, not for issue"
                    onClick={() => void previewPdf()}>
              <Eye className="h-4 w-4" />
              {pdfBusy ? 'Preparing…' : 'Preview PDF'}
            </button>
          )}
          {['DRAFT', 'RETURNED'].includes(w.status)
            && serviceCan(profile?.role, 'createWorkOrder') && (
            <Link to={`/service/work-orders/${w.id}/edit`} className="btn-secondary btn-sm">
              <Pencil className="h-4 w-4" /> Edit
            </Link>
          )}
          {/* 0219: any status - most often a cancelled order re-issued with one
              thing corrected. */}
          {serviceCan(profile?.role, 'createWorkOrder') && (
            <button className="btn-secondary btn-sm" disabled={busy}
                    onClick={() => setDuplicating(true)}>
              <Copy className="h-4 w-4" /> Duplicate
            </button>
          )}
          {/* A LIVE order can be varied; a draft is edited instead, which is
              what the button above is for. If one is already open on this
              order the button points AT it rather than offering a second —
              rpc_vo_raise refuses two, and an offer that always fails is
              worse than no offer. */}
          {['APPROVED', 'VENDIN', 'VENDACK', 'PARTIALLY_RECEIVED'].includes(w.status)
            && serviceCan(profile?.role, 'raiseVariation') && (
            vpos[0]?.open_variation_id
              ? (
                <Link to={`/service/variations/${vpos[0].open_variation_id}`}
                      className="btn-secondary btn-sm">
                  <ListChecks className="h-4 w-4" /> {vpos[0].open_variation_id} is open
                </Link>
              ) : (
                <button className="btn-secondary btn-sm" disabled={busy}
                        onClick={() => setRaising(true)}>
                  <ListChecks className="h-4 w-4" /> Raise a variation
                </button>
              )
          )}
          {/* Despatch stays with Procurement even though the Project Head may
              now RAISE an order. rpc_wo_send_to_contractor says the same, and a
              button that always fails is worse than no button. */}
          {w.status === 'APPROVED' && ['procurement_manager', 'procurement_head', 'admin']
              .includes(profile?.role ?? '') && (
            <button className="btn-primary btn-sm" disabled={busy} onClick={() => void despatch()}>
              <Send className="h-4 w-4" /> Send to contractor
            </button>
          )}
          {w.status === 'VENDIN' && ['procurement_manager', 'procurement_head',
              'site_construction_manager', 'admin'].includes(profile?.role ?? '') && (
            <button className="btn-primary btn-sm" disabled={busy} onClick={() => setAcking(true)}>
              <Stamp className="h-4 w-4" /> Record acknowledgement
            </button>
          )}
        </div>
      </div>

      {/* The chain sits above the detail: the first question anybody opening a
          work order asks is "where has this got to". */}
      <ApprovalChain docType="WO" docId={w.id} status={w.status}
                     currentStep={w.current_step}
                     sitePmId={siteRow[0]?.project_manager_id}
                     createdBy={raw[0]?.created_by}
                     onChanged={refresh} />

      {/* Ending it — 0156, widened by 0172.
          Below the chain, because an order is ended after it has been through
          the chain, not instead of going through it.

          The gate used to be the acknowledged statuses alone, and the comment
          here said "before that there is nothing to close, only something to
          cancel" — then 0172 built cancelling and nobody came back to widen
          it, so the Cancel button sat inside a card an APPROVED order never
          rendered. Reported 21-09-2026 on WO-T12-20260921-001.

          Every status except the terminal ones now shows it, and the card
          decides for itself whether this is an order to close or one to
          cancel. */}
      {/* 0177: the schedule, and the RCM's signature on each stage. Draws
          nothing at all unless the order has a lumpsum line, so the measured
          orders that make up most of this register are unchanged. Placed ABOVE
          the close-out card because an uncertified stage is a reason the order
          is still open, and the eye should meet it first. */}
      <WoMilestones woId={w.id} woStatus={w.status} onChanged={refresh} />

      {/* 0214: machines on hire, and their log sheets. Draws nothing unless
          the order has a hire line. */}
      <WoHire woId={w.id} woStatus={w.status} />

      <WoCloseOut woId={w.id} onChanged={refresh} />

      <Modal open={duplicating} onClose={() => setDuplicating(false)}
             title={`Duplicate ${w.id}`}
             subtitle="A new work order, in DRAFT, raised by you. This order stays exactly as it is."
             footer={
               <>
                 <button className="btn-secondary" onClick={() => setDuplicating(false)}>Cancel</button>
                 <button className="btn-primary" disabled={busy} onClick={() => void duplicate()}>
                   <Copy className="h-4 w-4" /> {busy ? 'Copying…' : 'Duplicate it'}
                 </button>
               </>
             }>
        <div className="space-y-2 text-sm text-slate-700">
          <p>
            <strong>Copied:</strong> the contractor, site, title, scope, quotation, dates,
            deductions, payment terms, every line (with milestones, hire details and
            measurement rules), this order&rsquo;s terms with their ticks and edits, and its
            papers.
          </p>
          <p>
            <strong>Not copied:</strong> the number, status, approvals, history,
            measurements, hire logs, bills, payments, variations, and the contractor&rsquo;s
            signed copy &mdash; they belong to this order.
          </p>
          <p className="text-xs text-slate-500">
            You go straight to the new draft to correct it. It then goes through the full
            approval chain as a new order.
          </p>
        </div>
      </Modal>

      <Modal open={acking} onClose={() => setAcking(false)}
             title="Record the contractor's acknowledgement"
             subtitle="Only after this may work be measured against the order. A contract nobody has signed is not a contract."
             footer={
               <>
                 <button className="btn-secondary" onClick={() => setAcking(false)}>Cancel</button>
                 <button className="btn-primary" disabled={busy} onClick={() => void acknowledge()}>
                   Record it
                 </button>
               </>
             }>
        <p className="mb-2 text-sm text-slate-600">
          His reference, if he gave one — a letter number, an email date, or just
          &ldquo;signed copy received at site&rdquo;.
        </p>
        <Input value={ackRef} onChange={(e) => setAckRef(e.target.value)}
               placeholder="Signed copy received at site, 14-09-2026" />
      </Modal>

      <Modal open={raising} onClose={() => setRaising(false)}
             title={`Raise a variation on ${w.id}`}
             subtitle="The lines go on the next screen. Nothing changes on the order until it is approved."
             footer={
               <>
                 <button className="btn-secondary" onClick={() => setRaising(false)}>Cancel</button>
                 <button className="btn-primary" disabled={busy || voReason.trim().length < 5}
                         onClick={() => void raiseVariation()}>
                   Raise it
                 </button>
               </>
             }>
        <Field label="Why is it needed" required
               hint="The first thing every approver reads. What changed on site, who agreed it, and when.">
          <Textarea rows={3} value={voReason} onChange={(e) => setVoReason(e.target.value)}
                    placeholder="Rock encountered below 1.5m in the footing pits; excavation quantity rises and a rock-cutting item is added, agreed at site with the PM on 12-09-2026." />
        </Field>
      </Modal>

      <Card title="The contract">
        <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
          <D label="Contractor" value={<>{w.vendor_name}<br />
            <span className="text-xs text-slate-500">{w.vendor_gstin ?? 'not GST registered'}</span></>} />
          <D label="PAN" value={w.vendor_pan ?? (
            <span className="text-amber-700">none on file — TDS would be 20%</span>)} />
          {/* The derived sentence, not the header field. */}
          <D label="Nature"
             value={(composition as any[])[0]?.label
                    ?? w.contract_nature.replace(/_/g, ' ').toLowerCase()} />
          <D label="Quotation" value={w.quotation_ref} />
          <D label="Starts" value={w.start_date ? ddmmyyyy(w.start_date) : null} />
          <D label="Completes" value={<>{w.completion_date ? ddmmyyyy(w.completion_date) : '—'}
            {w.duration_days ? <span className="text-xs text-slate-500"> · {w.duration_days} days</span> : null}</>} />
          <D label="Contract value" value={<span className="font-semibold">{rupees(w.contract_value)}</span>} />
          <D label="GST" value={w.is_reverse_charge
            ? <span className="text-amber-700">Reverse charge — OVAL owes it</span>
            : n(w.gst_amount) === 0 && w.gst_treatment === 'NONE'
              // Zero because it was decided, not because it is missing.
              ? <span className="text-emerald-700">No tax arises</span>
              : <>{rupees(w.gst_amount)} <span className="text-xs text-slate-500">({w.gst_treatment})</span></>} />
          <D label="Total" value={<span className="font-semibold">{rupees(w.total_with_gst)}</span>} />
          {n(vpos[0]?.variation_value) !== 0 && (
            <D label="Varied by" value={
              <span className={n(vpos[0]?.variation_value) < 0
                ? 'font-semibold text-emerald-700' : 'font-semibold text-amber-700'}>
                {rupees(vpos[0]?.variation_value)}
                <span className="block text-xs font-normal text-slate-500">
                  {vpos[0]?.approved_count} approved variation
                  {n(vpos[0]?.approved_count) === 1 ? '' : 's'}
                </span>
              </span>} />
          )}
          {n(vpos[0]?.variation_value) !== 0 && (
            <D label="Worth now" value={
              <span className="font-semibold">{rupees(vpos[0]?.revised_value)}</span>} />
          )}
        </dl>
        <TaxTreatment wo={w} rcm={rcm as any[]} />
        {n(vpos[0]?.variation_value) !== 0 && (
          <p className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
            The <strong>contract value</strong> is what was signed and does not change.
            The advance is a percentage of it and the printed order carries it.
            &ldquo;Worth now&rdquo; is that figure plus every approved variation.
          </p>
        )}
      </Card>

      <Card title="Deductions agreed on this contract"
            subtitle="Applied to every running account bill. Nobody types them per bill.">
        <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
          <D label="Retention" value={`${n(w.retention_pct)}% of each bill`} />
          <D label="Defect liability" value={w.defect_liability_months
            ? `${w.defect_liability_months} months after completion` : 'not set'} />
          <D label="TDS" value={w.tds_pct != null
            ? <>{n(w.tds_pct)}% <span className="text-xs text-slate-500">under {w.tds_section}</span></>
            : <span className="text-amber-700">no PAN — cannot be worked out</span>} />
          <D label="Advance" value={n(w.advance_pct) > 0
            ? `${n(w.advance_pct)}%, recovered at ${n(w.advance_recovery_pct)}% per bill`
            : 'none'} />
        </dl>
        {/* 0171: when he gets paid. Its own line rather than a fifth cell —
            it is a sentence, and the four above are figures. */}
        <div className="mt-3 border-t border-slate-100 pt-3">
          <D label="Payment terms" value={w.payment_terms
            ? <span className="font-medium">{w.payment_terms}</span>
            : <span className="text-amber-700">not stated on this order</span>} />
        </div>
      </Card>

      <Card title={linesView === 'MONEY' ? 'Lines, with their GST' : 'Lines, and how far along each one is'}
            subtitle={linesView === 'MONEY'
              ? 'Each line’s value as ordered, its GST split and its total. The totals agree with the order header.'
              : 'Ordered, what variations allowed, what has been certified in the Measurement Book.'}
            actions={(rcm as any[]).length > 0
              ? <LinesViewSwitch view={linesView} onChange={setLinesView} />
              : undefined}>
        {linesView === 'MONEY' && (rcm as any[]).length > 0 ? (
          <LineGstTable
            treatment={w.gst_treatment}
            header={{ gst_treatment: w.gst_treatment, is_reverse_charge: w.is_reverse_charge,
                      gst_amount: n(w.gst_amount), contract_value: n(w.contract_value),
                      total_with_gst: n(w.total_with_gst) }}
            rows={(rcm as any[]).map((r) => ({
              key: r.wo_line_id, sl: r.sl_no, title: r.line_description,
              subtitle: (() => {
                const p = lines.find((x) => x.wo_line_id === r.wo_line_id)
                return p ? `${qty(p.qty_ordered)} ${p.uom} × ${rupees(p.rate)}` : null
              })(),
              split: gstSplit(n(r.line_amount), n(r.line_gst_rate),
                gstPayer({ gst_treatment: w.gst_treatment, is_reverse_charge: w.is_reverse_charge,
                           gst_amount: n(w.gst_amount) }, r.verdict_notified),
                w.gst_treatment),
            }))} />
        ) : (<>
        <div className="overflow-x-auto">
          <table className="tbl">
            <thead>
              <tr>
                <th>#</th><th>Description</th><th>Basis</th><th>UOM</th>
                <th className="num">Rate</th><th className="num">Ordered</th>
                <th className="num">Allowed</th><th className="num">Certified</th>
                <th className="num">Left</th><th className="num">Ready to bill</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l.wo_line_id}>
                  <td>{l.sl_no}</td>
                  <td className="max-w-[240px]">
                    {l.description}
                    {bornOf[l.wo_line_id] && (
                      <Link to={`/service/variations/${bornOf[l.wo_line_id]}`}
                            className="ml-1 pill-blue hover:underline"
                            title="This item was not on the order as signed">
                        by {bornOf[l.wo_line_id]}
                      </Link>
                    )}
                  </td>
                  <td className="whitespace-nowrap text-xs">{BASIS_LABEL[l.pricing_basis] ?? l.pricing_basis}</td>
                  <td>{l.uom}</td>
                  <td className="num">{rupees(l.rate)}</td>
                  <td className="num">{qty(l.qty_ordered)}</td>
                  <td className="num">{qty(l.qty_allowed)}</td>
                  <td className="num font-semibold">{qty(l.qty_certified)}</td>
                  <td className="num">{qty(l.qty_left_to_measure)}</td>
                  <td className="num whitespace-nowrap">{rupees(l.value_ready_to_bill)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {lines.length > 0 && (
          <p className="mt-3 flex items-center gap-1.5 text-xs text-slate-500">
            <Ruler className="h-3.5 w-3.5" />
            &ldquo;Allowed&rdquo; is what was ordered plus every approved variation. A measurement
            cannot be certified past it.
          </p>
        )}
        </>)}
      </Card>

      {/* CMD, 31-10-2026: what a hire order pays each month at full use.
          Draws nothing unless the order has a hire line. */}
      <WoHirePayout woId={w.id} />

      {variations.length > 0 && (
        <Card title="Variations on this order"
              subtitle="What was added or taken away after the order was signed, and who approved it.">
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Variation</th><th className="num">No.</th><th>Reason</th>
                  <th className="num">Value</th><th>Status</th><th>Raised</th>
                </tr>
              </thead>
              <tbody>
                {(variations as any[]).map((v) => (
                  <tr key={v.id}>
                    <td className="whitespace-nowrap font-medium">
                      <Link to={`/service/variations/${v.id}`}
                            className="text-amber-700 hover:underline">{v.id}</Link>
                    </td>
                    <td className="num">{v.variation_no}</td>
                    <td className="max-w-[280px] truncate">{v.reason}</td>
                    <td className={n(v.variation_value) < 0
                      ? 'num whitespace-nowrap text-emerald-700'
                      : 'num whitespace-nowrap'}>{rupees(v.variation_value)}</td>
                    <td><StatusPill status={v.status} /></td>
                    <td className="whitespace-nowrap text-xs">{ddmmyyyy(v.date_created)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 flex items-center gap-1.5 text-xs text-slate-500">
            <ListChecks className="h-3.5 w-3.5" />
            Only an APPROVED variation moves the &ldquo;Allowed&rdquo; column above. One that
            is still in the chain changes nothing at site.
          </p>
        </Card>
      )}

      {/* The contractor's papers, and — once he has acknowledged it — the
          scanned copy of the order he signed. Renders nothing at all for a role
          that may not read them; see WoPapers. */}
      {/* The order's OWN copy of its terms, frozen when it was raised. */}
      {/* WHY THIS IS NOT HARD-CODED false ANY MORE.
          It was, and the consequence was reported from the test site: the
          buyer saves a draft, lands on THIS page, sees all 79 clauses listed
          with no tick against any of them, and has nowhere to say which ones
          go on the contract. The checkboxes existed — they were only ever
          drawn on the builder, which he had just left.
          Same condition as the Edit button a few lines above: a draft this
          person may write is a draft whose clauses he may tick. Anything
          submitted or beyond stays read-only, because by then they are part
          of a contract somebody has approved. */}
      <WoTerms woId={w.id}
               editable={['DRAFT', 'RETURNED'].includes(w.status)
                         && serviceCan(profile?.role, 'createWorkOrder')} />

      <WoPapers woId={w.id} status={w.status} />

      {/*
        The same audit trail every requisition and purchase order has carried
        since August, over the same table. rpc_works_action has been writing
        document_audit rows for work orders since 0120 — submissions, every
        stage cleared, returns, rejections, despatch and acknowledgement — and
        nothing on this screen was drawing them.

        It sits BELOW the approval chain and the papers on purpose. The chain
        answers "where has this got to", which is what people open a work order
        to find out; the trail answers "what happened to it", which is what they
        come back for when something has gone wrong.
      */}
      <AuditTrail docType="WO" docId={w.id} />

      <PdfPreviewPane preview={preview} onClose={closePreview} />
    </div>
  )
}
